import React, { useMemo, useState } from 'react';
import { GripVertical, MoveUp, MoveDown, Edit2, X, Check, Trash2, Plus } from 'lucide-react';
import DecisionPathEditor, { type DecisionPathFields } from './DecisionPathEditor';
import {
  existingRowFindingsForStep,
  findingReason,
  isBoundaryStep,
  overlayFindings,
  requiresBothDecisionPaths,
  stakeholderFindings,
  stepTypeOf,
  type AccuracyGateFinding,
} from '../../utils/accuracyGate';
import { makeManualStep } from '../../utils/applyAccuracyGateFinding';

interface Process {
  id: string;
  name: string;
  step_number: string;
  assignee?: string;
  description?: string;
  [key: string]: any;
}

export type FindingAction = 'approve' | 'adjust' | 'reject' | 'keep_flagged';

export interface FindingActionOverrides {
  name?: string;
  description?: string;
  assignee?: string;
  isDecision?: boolean;
  decisionPaths?: { yes?: string; no?: string; yes_label?: string; no_label?: string };
}

interface ReorderableProcessListProps {
  processes: Process[];
  onReorder: (reorderedProcesses: Process[]) => void;
  availableStakeholders?: string[];
  onStepEdit?: (index: number, updatedStep: Process) => void;
  onStepDelete?: (index: number, step: Process) => void;
  onStepAdd?: (insertIndex: number, newStep: Process) => void;
  onPendingDeleteChange?: (markedIds: string[]) => void;
  findings?: AccuracyGateFinding[];
  sourceHasDiamonds?: boolean;
  onFindingAction?: (finding: AccuracyGateFinding, action: FindingAction, overrides?: FindingActionOverrides) => void;
  onDecisionEdit?: (index: number, fields: { isDecision: boolean; decisionPaths?: FindingActionOverrides['decisionPaths'] }) => void;
}

function dedupeDescription(text: string): string {
  if (typeof text !== 'string' || !text) return text || '';
  const parts = text.split(/:\s*/);
  const out: string[] = [];
  for (const part of parts) {
    if (out.length === 0 || out[out.length - 1].trim() !== part.trim()) {
      out.push(part);
    }
  }
  return out.join(': ').trim();
}

function emptyDecision(): DecisionPathFields {
  return { isDecision: false, yes: '', no: '', yesLabel: '', noLabel: '' };
}

function decisionFromStep(step: Process | null | undefined): DecisionPathFields {
  if (!step) return emptyDecision();
  const dp = step.decision_paths || step.decisionPaths || {};
  const isDecision = stepTypeOf(step) === 'decision' || step.is_decision === true;
  return {
    isDecision,
    yes: dp.yes != null ? String(dp.yes) : '',
    no: dp.no != null ? String(dp.no) : '',
    yesLabel: dp.yes_label || dp.yes_condition || '',
    noLabel: dp.no_label || dp.no_condition || '',
  };
}

function decisionFromFinding(finding: AccuracyGateFinding): DecisionPathFields {
  const dp = finding.proposal?.decision_paths || {};
  const isDecision = finding.proposal?.step_type === 'decision'
    || finding.fix === 'mark_decision'
    || finding.fix === 'set_decision_paths';
  return {
    isDecision,
    yes: dp.yes != null ? String(dp.yes) : '',
    no: dp.no != null ? String(dp.no) : '',
    yesLabel: dp.yes_label || dp.yes_condition || '',
    noLabel: dp.no_label || dp.no_condition || '',
  };
}

function pathsFromFields(d: DecisionPathFields) {
  return { yes: d.yes, no: d.no, yes_label: d.yesLabel, no_label: d.noLabel };
}

type DisplayItem =
  | { kind: 'step'; process: Process; index: number }
  | { kind: 'overlay'; finding: AccuracyGateFinding };

function buildDisplayItems(processes: Process[], findings: AccuracyGateFinding[]): DisplayItem[] {
  const overlays = overlayFindings(findings);
  const used = new Set<string>();
  const items: DisplayItem[] = [];
  processes.forEach((process, index) => {
    items.push({ kind: 'step', process, index });
        overlays.filter((f) => {
      const after = f.proposal?.insert_after_step_id || f.target?.insert_after_step_id;
      return after != null && (String(after) === String(process.id) || String(after) === String(process.step_number));
    }).forEach((f) => {
      used.add(f.id);
      items.push({ kind: 'overlay', finding: f });
    });
  });
  overlays.filter((f) => !used.has(f.id)).forEach((f) => {
    const endIdx = items.map((it, i) => (it.kind === 'step' && stepTypeOf(it.process) === 'end' ? i : -1)).filter((i) => i >= 0);
    const insertAt = endIdx.length ? endIdx[endIdx.length - 1] : items.length;
    items.splice(insertAt, 0, { kind: 'overlay', finding: f });
  });
  return items;
}

const ReorderableProcessList: React.FC<ReorderableProcessListProps> = ({
  processes,
  onReorder,
  availableStakeholders = [],
  onStepEdit,
  onStepDelete,
  onStepAdd,
  onPendingDeleteChange,
  findings = [],
  sourceHasDiamonds = false,
  onFindingAction,
  onDecisionEdit,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; description: string; assignee: string }>({
    name: '', description: '', assignee: '',
  });
  const [editDecision, setEditDecision] = useState<DecisionPathFields>(emptyDecision());
  const [addingAtIndex, setAddingAtIndex] = useState<number | null>(null);
  const [addForm, setAddForm] = useState<{ name: string; description: string; assignee: string }>({
    name: '', description: '', assignee: '',
  });
  const [addDecision, setAddDecision] = useState<DecisionPathFields>(emptyDecision());
  const [adjustingFindingId, setAdjustingFindingId] = useState<string | null>(null);
  const [adjustForm, setAdjustForm] = useState<{ name: string; description: string; assignee: string }>({
    name: '', description: '', assignee: '',
  });
  const [adjustDecision, setAdjustDecision] = useState<DecisionPathFields>(emptyDecision());
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    index: number;
    step: Process | null;
  }>({ isOpen: false, index: -1, step: null });
  const [markedForDeletion, setMarkedForDeletion] = useState<Set<string>>(new Set());

  const deferredDelete = !!onPendingDeleteChange;
  const displayItems = useMemo(() => buildDisplayItems(processes, findings), [processes, findings]);
  const stakeholderProposals = useMemo(() => stakeholderFindings(findings), [findings]);
  const stepOptions = processes.map((p) => ({ id: String(p.id), name: p.name || String(p.id) }));

  const toggleMarkDelete = (step: Process) => {
    const id = String(step.id);
    setMarkedForDeletion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      if (onPendingDeleteChange) onPendingDeleteChange(Array.from(next));
      return next;
    });
  };

  const isStartStep = (index: number) => index === 0 || stepTypeOf(processes[index]) === 'start';
  const isEndStep = (index: number) => index === processes.length - 1 || stepTypeOf(processes[index]) === 'end';
  const isLockedStep = (index: number) => {
    if (index < 0 || index >= processes.length) return true;
    return isStartStep(index) || isEndStep(index) || isBoundaryStep(processes[index]);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (isLockedStep(index)) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    if (isLockedStep(index)) return;
    setDragOverIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    if (isLockedStep(dropIndex)) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const newProcesses = [...processes];
    const draggedItem = newProcesses[draggedIndex];
    newProcesses.splice(draggedIndex, 1);
    let finalDropIndex = dropIndex;
    if (draggedIndex < dropIndex) finalDropIndex = dropIndex - 1;
    if (finalDropIndex === 0) finalDropIndex = 1;
    else if (finalDropIndex >= processes.length - 1) finalDropIndex = processes.length - 2;
    newProcesses.splice(finalDropIndex, 0, draggedItem);
    onReorder(newProcesses);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const moveUp = (index: number) => {
    if (index <= 1 || isLockedStep(index)) return;
    const newProcesses = [...processes];
    [newProcesses[index - 1], newProcesses[index]] = [newProcesses[index], newProcesses[index - 1]];
    onReorder(newProcesses);
  };

  const moveDown = (index: number) => {
    if (index >= processes.length - 2 || isLockedStep(index)) return;
    const newProcesses = [...processes];
    [newProcesses[index], newProcesses[index + 1]] = [newProcesses[index + 1], newProcesses[index]];
    onReorder(newProcesses);
  };

  const handleEditClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const step = processes[index];
    setEditingIndex(index);
    setEditForm({
      name: step.name || '',
      description: dedupeDescription(step.description || step.full_text || ''),
      assignee: step.actor || step.assignee || step.swimlane || '',
    });
    setEditDecision(decisionFromStep(step));
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm({ name: '', description: '', assignee: '' });
    setEditDecision(emptyDecision());
  };

  const handleSaveEdit = (index: number) => {
    const updatedStep = {
      ...processes[index],
      name: editForm.name,
      description: editForm.description,
      full_text: editForm.description,
      assignee: editForm.assignee,
      actor: editForm.assignee,
      swimlane: editForm.assignee,
    };
    if (onStepEdit) onStepEdit(index, updatedStep);
    else {
      const newProcesses = [...processes];
      newProcesses[index] = updatedStep;
      onReorder(newProcesses);
    }
    if (onDecisionEdit) {
      onDecisionEdit(index, { isDecision: editDecision.isDecision, decisionPaths: pathsFromFields(editDecision) });
    }
    setEditingIndex(null);
    setEditForm({ name: '', description: '', assignee: '' });
    setEditDecision(emptyDecision());
  };

  const handleDeleteClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLockedStep(index)) return;
    if (deferredDelete) {
      toggleMarkDelete(processes[index]);
      if (editingIndex === index) handleCancelEdit();
      return;
    }
    setDeleteConfirmation({ isOpen: true, index, step: processes[index] });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.step && onStepDelete) {
      onStepDelete(deleteConfirmation.index, deleteConfirmation.step);
    }
    setDeleteConfirmation({ isOpen: false, index: -1, step: null });
  };

  const openAddForm = (insertIndex: number) => {
    if (editingIndex !== null) return;
    const clamped = Math.min(Math.max(insertIndex, 1), processes.length - 1);
    setAddingAtIndex(clamped);
    setAddForm({ name: '', description: '', assignee: '' });
    setAddDecision(emptyDecision());
  };

  const handleCancelAdd = () => {
    setAddingAtIndex(null);
    setAddForm({ name: '', description: '', assignee: '' });
    setAddDecision(emptyDecision());
  };

  const handleConfirmAdd = () => {
    if (addingAtIndex === null) return;
    if (!addForm.name.trim()) return;
    const newStep = makeManualStep({
      name: addForm.name,
      description: addForm.description,
      assignee: addForm.assignee,
      isDecision: addDecision.isDecision,
      decisionPaths: addDecision.isDecision ? pathsFromFields(addDecision) : null,
    });
    if (onStepAdd) onStepAdd(addingAtIndex, newStep);
    else {
      const newProcesses = [...processes];
      newProcesses.splice(addingAtIndex, 0, newStep);
      onReorder(newProcesses);
    }
    if (onDecisionEdit && addDecision.isDecision) {
      onDecisionEdit(addingAtIndex, { isDecision: true, decisionPaths: pathsFromFields(addDecision) });
    }
    setAddingAtIndex(null);
    setAddForm({ name: '', description: '', assignee: '' });
    setAddDecision(emptyDecision());
  };

  const openAdjust = (finding: AccuracyGateFinding, existing?: Process) => {
    setAdjustingFindingId(finding.id);
    setAdjustForm({
      name: (existing?.name || finding.proposal?.name || finding.source?.text || '') as string,
      description: (existing?.description || finding.proposal?.description || finding.source?.quote || '') as string,
      assignee: (existing?.actor || existing?.assignee || finding.proposal?.actor || finding.source?.lane || '') as string,
    });
    setAdjustDecision(existing ? { ...decisionFromStep(existing), ...decisionFromFinding(finding), isDecision: decisionFromFinding(finding).isDecision || decisionFromStep(existing).isDecision } : decisionFromFinding(finding));
  };

  const cancelAdjust = () => {
    setAdjustingFindingId(null);
    setAdjustForm({ name: '', description: '', assignee: '' });
    setAdjustDecision(emptyDecision());
  };

  const commitFinding = (finding: AccuracyGateFinding, action: FindingAction, withForm = false) => {
    if (!onFindingAction) return;
    if (action === 'approve' && requiresBothDecisionPaths(finding, sourceHasDiamonds)) {
      const paths = withForm ? pathsFromFields(adjustDecision) : (finding.proposal?.decision_paths || {});
      if (!paths.yes || !paths.no || String(paths.yes) === String(paths.no)) {
        openAdjust(finding);
        return;
      }
    }
    const overrides = withForm ? {
      name: adjustForm.name,
      description: adjustForm.description,
      assignee: adjustForm.assignee,
      isDecision: adjustDecision.isDecision,
      decisionPaths: pathsFromFields(adjustDecision),
    } : undefined;
    onFindingAction(finding, action, overrides);
    cancelAdjust();
  };

  const renderAssignee = (
    value: string,
    onChange: (v: string) => void,
    ring: string,
  ) => (
    availableStakeholders.length > 0 ? (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 ${ring}`}
      >
        <option value="">Select stakeholder...</option>
        {availableStakeholders.map((stakeholder) => (
          <option key={stakeholder} value={stakeholder}>{stakeholder}</option>
        ))}
      </select>
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 ${ring}`}
        placeholder="Enter assignee name"
      />
    )
  );

  const renderDecisionBlock = (
    fields: DecisionPathFields,
    onChange: (f: DecisionPathFields) => void,
    selfId?: string,
    suggestedYesId?: string,
  ) => (
    <DecisionPathEditor
      fields={fields}
      steps={stepOptions}
      selfId={selfId}
      suggestedYesId={suggestedYesId}
      onChange={onChange}
    />
  );

  const renderAddForm = () => (
    <div className="rounded-lg border-2 border-green-500 shadow-lg bg-green-50">
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-900">
            Add Step {addingAtIndex !== null ? `at position #${addingAtIndex}` : ''}
          </h4>
          <div className="flex items-center space-x-1">
            <button
              onClick={handleConfirmAdd}
              disabled={!addForm.name.trim()}
              className="p-1.5 rounded bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
              title="Add step"
            >
              <Check className="w-4 h-4" />
            </button>
            <button onClick={handleCancelAdd} className="p-1.5 rounded bg-gray-400 hover:bg-gray-500 text-white transition-colors" title="Cancel">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Step Name</label>
          <input
            type="text"
            autoFocus
            value={addForm.name}
            onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter step name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={addForm.description}
            onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter step description"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Assignee / Stakeholder</label>
          {renderAssignee(addForm.assignee, (v) => setAddForm({ ...addForm, assignee: v }), 'focus:ring-green-500')}
        </div>
        {renderDecisionBlock(addDecision, setAddDecision, undefined, processes[addingAtIndex || 0]?.id)}
      </div>
    </div>
  );

  const renderInsertHere = (insertIndex: number) => {
    if (!onStepAdd && !onReorder) return null;
    if (addingAtIndex === insertIndex) return renderAddForm();
    return (
      <div className="group flex items-center justify-center h-2 hover:h-8 transition-all">
        <button
          onClick={() => openAddForm(insertIndex)}
          disabled={editingIndex !== null || addingAtIndex !== null}
          className="opacity-0 group-hover:opacity-100 disabled:hidden inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-opacity"
          title="Insert a step here"
        >
          <Plus className="w-3 h-3" /> Insert step
        </button>
      </div>
    );
  };

  const renderFindingActions = (finding: AccuracyGateFinding, existing?: Process) => {
    const isFlag = finding.fix === 'flag_for_sme';
    const isAdjusting = adjustingFindingId === finding.id;
    return (
      <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
        {isFlag ? (
          <button
            onClick={() => commitFinding(finding, 'keep_flagged')}
            className="px-2 py-1 rounded text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200"
          >
            Keep flagged
          </button>
        ) : (
          <button
            onClick={() => {
              if (finding.fix === 'delete_step' && existing) {
                toggleMarkDelete(existing);
                commitFinding(finding, 'approve');
                return;
              }
              commitFinding(finding, 'approve');
            }}
            className="px-2 py-1 rounded text-xs font-semibold text-white bg-green-600 hover:bg-green-700"
          >
            Approve
          </button>
        )}
        <button
          onClick={() => (isAdjusting ? cancelAdjust() : openAdjust(finding, existing))}
          className="px-2 py-1 rounded text-xs font-semibold text-indigo-800 bg-indigo-100 hover:bg-indigo-200"
        >
          Adjust
        </button>
        <button
          onClick={() => commitFinding(finding, 'reject')}
          className="px-2 py-1 rounded text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200"
        >
          Reject
        </button>
      </div>
    );
  };

  const renderAdjustForm = (finding: AccuracyGateFinding, existing?: Process) => (
    <div className="p-3 space-y-2 bg-amber-50/80 border-t border-dashed border-amber-400">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Step Name</label>
        <input
          type="text"
          value={adjustForm.name}
          onChange={(e) => setAdjustForm({ ...adjustForm, name: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={adjustForm.description}
          onChange={(e) => setAdjustForm({ ...adjustForm, description: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Assignee / Stakeholder</label>
        {renderAssignee(adjustForm.assignee, (v) => setAdjustForm({ ...adjustForm, assignee: v }), 'focus:ring-amber-500')}
      </div>
      {renderDecisionBlock(adjustDecision, setAdjustDecision, existing?.id, existing ? processes[processes.findIndex((p) => p.id === existing.id) + 1]?.id : undefined)}
      <div className="flex justify-end gap-2">
        <button onClick={cancelAdjust} className="px-3 py-1.5 text-xs rounded bg-gray-200 text-gray-700">Cancel</button>
        <button
          onClick={() => commitFinding(finding, 'adjust', true)}
          className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white"
        >
          Save adjustment
        </button>
      </div>
    </div>
  );

  return (
    <>
      {stakeholderProposals.length > 0 && (
        <div className="mb-3 space-y-2">
          {stakeholderProposals.map((finding) => (
            <div key={finding.id} className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 p-3 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">Add stakeholder (no new swimlane)</div>
                <div className="text-xs text-gray-600">{findingReason(finding)}</div>
              </div>
              {renderFindingActions(finding)}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {displayItems.map((item) => {
          if (item.kind === 'overlay') {
            const finding = item.finding;
            const typeLabel = finding.proposal?.step_type || 'process';
            const isAdjusting = adjustingFindingId === finding.id;
            return (
              <div
                key={`overlay-${finding.id}`}
                data-finding-id={finding.id}
                className="rounded-lg border-2 border-dashed border-indigo-400 bg-indigo-50/70"
              >
                <div className="flex items-start gap-3 p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-indigo-700">Proposed {typeLabel}</div>
                    <div className="text-sm font-semibold text-gray-900 truncate">{finding.proposal?.name || finding.source?.text}</div>
                    <div className="text-xs text-gray-600 truncate">{finding.proposal?.actor || finding.source?.lane || ''}</div>
                    <div className="text-xs text-indigo-800 mt-1">{findingReason(finding)}</div>
                  </div>
                  {renderFindingActions(finding)}
                </div>
                {isAdjusting && renderAdjustForm(finding)}
              </div>
            );
          }

          const { process, index } = item;
          const locked = isLockedStep(index);
          const isEditing = editingIndex === index;
          const isMarked = markedForDeletion.has(String(process.id));
          const rowFindings = existingRowFindingsForStep(findings, String(process.id));
          const hasProposal = rowFindings.length > 0;
          const showInsertBefore = index >= 1;
          const suggestedYes = processes[index + 1]?.id;

          return (
            <React.Fragment key={process.id}>
              {showInsertBefore && renderInsertHere(index)}
              <div
                data-finding-id={rowFindings[0]?.id}
                className={`
                  rounded-lg border-2 transition-all
                  ${locked ? 'bg-gray-100 border-gray-300 opacity-60' : 'bg-white'}
                  ${draggedIndex === index ? 'opacity-50 border-electric-blue bg-blue-50' : ''}
                  ${dragOverIndex === index && draggedIndex !== index && !locked ? 'border-green-500 bg-green-50' : ''}
                  ${!locked && !draggedIndex && !dragOverIndex && !isEditing && !hasProposal ? 'border-gray-200 hover:shadow-md' : ''}
                  ${isEditing ? 'border-blue-500 shadow-lg' : ''}
                  ${isMarked ? 'border-red-300 bg-red-50 opacity-70' : ''}
                  ${hasProposal && !isEditing ? 'border-dashed border-amber-500 bg-amber-50/40' : ''}
                `}
              >
                {!isEditing && (
                  <div
                    draggable={!locked}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center space-x-3 p-3 ${locked ? 'cursor-not-allowed' : 'cursor-move'}`}
                  >
                    <GripVertical className={`w-5 h-5 flex-shrink-0 ${locked ? 'text-gray-300' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold truncate ${isMarked ? 'text-red-400 line-through' : 'text-gray-900'}`}>
                        {process.name}
                      </div>
                      {isMarked ? (
                        <div className="text-xs font-medium text-red-500">Marked for deletion — saves on “Finish / Save”</div>
                      ) : (process.actor || process.assignee || process.swimlane) && (
                        <div className="text-xs text-gray-500 truncate">
                          {process.actor || process.assignee || process.swimlane}
                          {stepTypeOf(process) === 'decision' && <span className="ml-2 text-indigo-600 font-medium">decision</span>}
                          {stepTypeOf(process) === 'annotation' && <span className="ml-2 text-gray-500 font-medium">annotation</span>}
                        </div>
                      )}
                      {rowFindings.map((f) => (
                        <div key={f.id} className="text-xs text-amber-800 mt-1">{findingReason(f)}</div>
                      ))}
                    </div>

                    {hasProposal && (
                      <div className="flex flex-col gap-1">
                        {rowFindings.map((f) => (
                          <div key={f.id}>{renderFindingActions(f, process)}</div>
                        ))}
                      </div>
                    )}

                    {!locked && isMarked && deferredDelete && (
                      <div className="flex items-center flex-shrink-0">
                        <button
                          onClick={(e) => handleDeleteClick(index, e)}
                          className="px-2 py-1 rounded text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors flex items-center gap-1"
                          title="Undo delete"
                        >
                          <X className="w-3.5 h-3.5" /> Undo
                        </button>
                      </div>
                    )}

                    {!locked && !isMarked && (
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <button onClick={(e) => handleEditClick(index, e)} className="p-1 rounded hover:bg-blue-100 transition-colors" title="Edit step">
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(index, e)}
                          className="p-1 rounded hover:bg-red-100 transition-colors"
                          title={deferredDelete ? 'Mark for deletion' : 'Delete step'}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                        <button onClick={() => moveUp(index)} disabled={index <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Move up">
                          <MoveUp className="w-4 h-4 text-gray-600" />
                        </button>
                        <button onClick={() => moveDown(index)} disabled={index >= processes.length - 2} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Move down">
                          <MoveDown className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    )}

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {locked && (
                        <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">
                          {isStartStep(index) ? 'START' : 'END'}
                        </span>
                      )}
                      <div className="text-sm font-mono text-gray-500">#{index}</div>
                    </div>
                  </div>
                )}

                {hasProposal && adjustingFindingId && rowFindings.some((f) => f.id === adjustingFindingId) && (
                  renderAdjustForm(rowFindings.find((f) => f.id === adjustingFindingId)!, process)
                )}

                {isEditing && (
                  <div className="p-4 space-y-3 bg-blue-50">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900">Edit Step #{index}</h4>
                      <div className="flex items-center space-x-1">
                        <button onClick={(e) => handleDeleteClick(index, e)} className="p-1.5 rounded bg-red-500 hover:bg-red-600 text-white transition-colors" title="Delete step">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleSaveEdit(index)} className="p-1.5 rounded bg-green-500 hover:bg-green-600 text-white transition-colors" title="Save changes">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="p-1.5 rounded bg-gray-400 hover:bg-gray-500 text-white transition-colors" title="Cancel">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Step Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter step name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter step description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Assignee / Stakeholder</label>
                      {renderAssignee(editForm.assignee, (v) => setEditForm({ ...editForm, assignee: v }), 'focus:ring-blue-500')}
                    </div>
                    {renderDecisionBlock(editDecision, setEditDecision, process.id, suggestedYes)}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {processes.length >= 1 && addingAtIndex === null && editingIndex === null && (
        <button
          onClick={() => openAddForm(processes.length - 1)}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed border-green-400 text-green-700 font-medium hover:bg-green-50 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Step
        </button>
      )}

      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Delete Step</h2>
              </div>
              <button onClick={() => setDeleteConfirmation({ isOpen: false, index: -1, step: null })} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">Are you sure you want to delete this step?</p>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 text-sm">{deleteConfirmation.step?.name}</p>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setDeleteConfirmation({ isOpen: false, index: -1, step: null })} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={handleConfirmDelete} className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700">
                Delete Step
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReorderableProcessList;
